/**
 * ForgotPasswordDialog — 2-step (request → reset) password recovery.
 *
 * Manages its own state and API calls. Parent only controls open/close and
 * passes the initial email (usually pre-filled from the login form).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import api from "@/api/client";

export function ForgotPasswordDialog({ open, onOpenChange, initialEmail = "", onResetSuccess }) {
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Reset all local state whenever the dialog is re-opened with a fresh email.
  useEffect(() => {
    if (open) {
      setStep("request");
      setEmail(initialEmail);
      setOtp("");
      setNewPw("");
      setNewPw2("");
      setResendCooldown(0);
    }
  }, [open, initialEmail]);

  // Countdown tick for the resend cooldown.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const submitRequest = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/password/forgot", { email: email.trim().toLowerCase() });
      toast.success("If a matching account exists, a reset code was sent (SMS + email).");
      setStep("reset");
      setResendCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset code");
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    try {
      await api.post("/auth/password/forgot", { email: email.trim().toLowerCase() });
      toast.success("A fresh reset code has been sent.");
      setOtp("");
      setResendCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend code");
      if (err.response?.status === 429) setResendCooldown(60);
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e?.preventDefault();
    if (otp.length !== 4) { toast.error("Enter the 4-digit code"); return; }
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (newPw !== newPw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/password/reset", {
        email: email.trim().toLowerCase(),
        otp,
        new_password: newPw,
      });
      toast.success("Password reset. Please sign in with your new password.");
      onOpenChange(false);
      onResetSuccess?.(email.trim().toLowerCase());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            {step === "request"
              ? "Enter your account email. We'll send a 4-digit code to the phone number on file."
              : "Enter the code we sent to your phone and choose a new password."}
          </DialogDescription>
        </DialogHeader>

        {step === "request" ? (
          <form onSubmit={submitRequest} className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="forgot-email-input"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="forgot-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !email.trim()} data-testid="forgot-send-code">
                {loading ? "Sending…" : "Send reset code"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={submitReset} className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Code sent to the phone <b>and email</b> for <span className="font-mono">{email}</span>.{" "}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => { setStep("request"); setOtp(""); }}
                data-testid="forgot-change-email"
              >
                Use a different email?
              </button>
            </div>
            <div>
              <Label>4-digit code</Label>
              <div className="mt-1 flex justify-center">
                <InputOTP maxLength={4} value={otp} onChange={setOtp} data-testid="forgot-otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
                <span>Didn&apos;t get the code?</span>
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={resendCooldown > 0 || loading}
                  className="font-semibold underline hover:text-foreground disabled:no-underline disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="forgot-resend-code"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : (loading ? "Sending…" : "Resend code")}
                </button>
              </div>
            </div>
            <div>
              <Label>New password</Label>
              <Input
                type="password"
                required
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="At least 8 characters"
                data-testid="forgot-newpw-input"
              />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input
                type="password"
                required
                value={newPw2}
                onChange={(e) => setNewPw2(e.target.value)}
                data-testid="forgot-newpw-confirm"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="forgot-cancel-2">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || otp.length !== 4 || newPw.length < 8 || newPw !== newPw2}
                data-testid="forgot-reset-submit"
              >
                {loading ? "Resetting…" : "Reset password"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
